/**
 * Shared localStorage utilities for assessment tools
 *
 * Provides consistent storage patterns for saving and loading assessment progress,
 * with version checking, age validation, and error handling.
 *
 * Used by: cybersecurity-maturity-assessment, compliance-checklist, and other assessment tools
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Base interface for saved progress data
 * All assessment tools should extend this interface with their specific data
 */
export interface SavedProgress {
  /** Storage format version (e.g., "1.0") */
  version: string;

  /** Unix timestamp (milliseconds) when progress was last saved */
  timestamp: number;

  /** Additional tool-specific data */
  [key: string]: unknown;
}

/**
 * Storage operation result
 */
export interface StorageResult<T> {
  /** Whether the operation succeeded */
  success: boolean;

  /** Data returned (if successful) */
  data?: T;

  /** Error message (if failed) */
  error?: string;

  /** Additional metadata about the operation */
  metadata?: {
    version?: string;
    timestamp?: number;
    age?: number;
    ageInDays?: number;
  };
}

// ============================================================================
// Constants
// ============================================================================

/** Default maximum age for saved progress (in days) */
const DEFAULT_MAX_AGE_DAYS = 30;

/** Milliseconds in one day */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ============================================================================
// Core Storage Functions
// ============================================================================

/**
 * Save assessment progress to localStorage
 *
 * Automatically adds version and timestamp to the saved data.
 * Handles SSR environments and localStorage errors gracefully.
 *
 * @param key - localStorage key to save under
 * @param data - Progress data to save (version and timestamp will be added)
 * @returns Storage operation result
 *
 * @example
 * ```ts
 * const result = saveAssessmentProgress('my-assessment-progress', {
 *   currentStep: 'profile',
 *   answers: { q1: 'yes', q2: 'no' }
 * });
 *
 * if (result.success) {
 *   console.log('Progress saved!');
 * }
 * ```
 */
export function saveAssessmentProgress<T extends Record<string, unknown>>(
  key: string,
  data: T
): StorageResult<SavedProgress> {
  // SSR check
  if (typeof window === 'undefined') {
    return {
      success: false,
      error: 'localStorage not available in server-side rendering',
    };
  }

  try {
    const progress: SavedProgress = {
      ...data,
      version: data.version as string || '1.0',
      timestamp: Date.now(),
    };

    localStorage.setItem(key, JSON.stringify(progress));

    return {
      success: true,
      data: progress,
      metadata: {
        version: progress.version,
        timestamp: progress.timestamp,
      },
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`Failed to save progress for key "${key}":`, error);

    return {
      success: false,
      error: `Failed to save progress: ${errorMessage}`,
    };
  }
}

/**
 * Load assessment progress from localStorage
 *
 * Performs version checking and age validation to ensure loaded data is compatible
 * and recent. Returns null if no valid progress is found.
 *
 * @param key - localStorage key to load from
 * @param version - Expected storage format version (e.g., "1.0")
 * @param maxAgeDays - Maximum age in days (default: 30)
 * @returns Storage operation result with loaded data
 *
 * @example
 * ```ts
 * interface MyProgress {
 *   currentStep: string;
 *   answers: Record<string, string>;
 * }
 *
 * const result = loadAssessmentProgress<MyProgress>(
 *   'my-assessment-progress',
 *   '1.0',
 *   7 // Only load progress from last 7 days
 * );
 *
 * if (result.success && result.data) {
 *   console.log('Loaded progress:', result.data);
 * }
 * ```
 */
export function loadAssessmentProgress<T extends SavedProgress>(
  key: string,
  version: string,
  maxAgeDays: number = DEFAULT_MAX_AGE_DAYS
): StorageResult<T> {
  // SSR check
  if (typeof window === 'undefined') {
    return {
      success: false,
      error: 'localStorage not available in server-side rendering',
    };
  }

  try {
    const saved = localStorage.getItem(key);

    if (!saved) {
      return {
        success: false,
        error: 'No saved progress found',
      };
    }

    const progress = JSON.parse(saved) as T;

    // Version check
    if (progress.version !== version) {
      console.log(`Version mismatch for key "${key}": expected ${version}, got ${progress.version}`);

      // Auto-cleanup old version
      localStorage.removeItem(key);

      return {
        success: false,
        error: `Version mismatch: expected ${version}, got ${progress.version}`,
        metadata: {
          version: progress.version,
        },
      };
    }

    // Age check
    const ageMs = Date.now() - progress.timestamp;
    const ageInDays = ageMs / MS_PER_DAY;

    if (ageInDays > maxAgeDays) {
      console.log(`Progress too old for key "${key}": ${ageInDays.toFixed(1)} days (max: ${maxAgeDays})`);

      // Auto-cleanup old progress
      localStorage.removeItem(key);

      return {
        success: false,
        error: `Progress too old: ${ageInDays.toFixed(1)} days (max: ${maxAgeDays})`,
        metadata: {
          version: progress.version,
          timestamp: progress.timestamp,
          age: ageMs,
          ageInDays,
        },
      };
    }

    return {
      success: true,
      data: progress,
      metadata: {
        version: progress.version,
        timestamp: progress.timestamp,
        age: ageMs,
        ageInDays,
      },
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`Failed to load progress for key "${key}":`, error);

    return {
      success: false,
      error: `Failed to load progress: ${errorMessage}`,
    };
  }
}

/**
 * Clear saved assessment progress from localStorage
 *
 * Can clear multiple related keys at once (e.g., progress and results).
 *
 * @param keys - localStorage key(s) to remove (string or array of strings)
 * @returns Storage operation result
 *
 * @example
 * ```ts
 * // Clear single key
 * clearAssessmentProgress('my-assessment-progress');
 *
 * // Clear multiple keys
 * clearAssessmentProgress([
 *   'my-assessment-progress',
 *   'my-assessment-results'
 * ]);
 * ```
 */
export function clearAssessmentProgress(
  keys: string | string[]
): StorageResult<void> {
  // SSR check
  if (typeof window === 'undefined') {
    return {
      success: false,
      error: 'localStorage not available in server-side rendering',
    };
  }

  try {
    const keyArray = Array.isArray(keys) ? keys : [keys];

    for (const key of keyArray) {
      localStorage.removeItem(key);
    }

    return {
      success: true,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Failed to clear progress:', error);

    return {
      success: false,
      error: `Failed to clear progress: ${errorMessage}`,
    };
  }
}

/**
 * Check if valid assessment progress exists
 *
 * Performs the same version and age checks as loadAssessmentProgress,
 * but doesn't return the data (useful for showing "resume" prompts).
 *
 * @param key - localStorage key to check
 * @param version - Expected storage format version
 * @param maxAgeDays - Maximum age in days (default: 30)
 * @returns true if valid progress exists, false otherwise
 *
 * @example
 * ```ts
 * if (hasValidProgress('my-assessment-progress', '1.0', 7)) {
 *   setShowResumePrompt(true);
 * }
 * ```
 */
export function hasValidProgress(
  key: string,
  version: string,
  maxAgeDays: number = DEFAULT_MAX_AGE_DAYS
): boolean {
  const result = loadAssessmentProgress(key, version, maxAgeDays);
  return result.success && result.data !== undefined;
}

/**
 * Get the timestamp when progress was last saved
 *
 * @param key - localStorage key to check
 * @returns Unix timestamp (milliseconds) or null if not found/invalid
 *
 * @example
 * ```ts
 * const lastSaved = getProgressTimestamp('my-assessment-progress');
 * if (lastSaved) {
 *   const date = new Date(lastSaved);
 *   console.log(`Last saved: ${date.toLocaleString()}`);
 * }
 * ```
 */
export function getProgressTimestamp(key: string): number | null {
  // SSR check
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const saved = localStorage.getItem(key);

    if (!saved) {
      return null;
    }

    const progress = JSON.parse(saved) as SavedProgress;
    return progress.timestamp || null;
  } catch (error) {
    console.error(`Failed to get timestamp for key "${key}":`, error);
    return null;
  }
}

/**
 * Get the version of saved progress
 *
 * @param key - localStorage key to check
 * @returns Version string or null if not found/invalid
 *
 * @example
 * ```ts
 * const version = getProgressVersion('my-assessment-progress');
 * console.log(`Saved version: ${version}`);
 * ```
 */
export function getProgressVersion(key: string): string | null {
  // SSR check
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const saved = localStorage.getItem(key);

    if (!saved) {
      return null;
    }

    const progress = JSON.parse(saved) as SavedProgress;
    return progress.version || null;
  } catch (error) {
    console.error(`Failed to get version for key "${key}":`, error);
    return null;
  }
}

/**
 * Get age of saved progress in days
 *
 * @param key - localStorage key to check
 * @returns Age in days or null if not found/invalid
 *
 * @example
 * ```ts
 * const age = getProgressAge('my-assessment-progress');
 * if (age !== null && age > 7) {
 *   console.log('Progress is more than a week old');
 * }
 * ```
 */
export function getProgressAge(key: string): number | null {
  const timestamp = getProgressTimestamp(key);

  if (timestamp === null) {
    return null;
  }

  const ageMs = Date.now() - timestamp;
  return ageMs / MS_PER_DAY;
}

/**
 * Get all saved progress keys with a specific prefix
 *
 * Useful for finding all assessment-related keys.
 * Note: This iterates through ALL localStorage keys, so use sparingly.
 *
 * @param prefix - Key prefix to search for (e.g., "assessment-")
 * @returns Array of matching keys
 *
 * @example
 * ```ts
 * // Find all assessment progress keys
 * const assessmentKeys = getAllProgressKeys('assessment-');
 * console.log(`Found ${assessmentKeys.length} saved assessments`);
 * ```
 */
export function getAllProgressKeys(prefix: string): string[] {
  // SSR check
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const keys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keys.push(key);
      }
    }

    return keys;
  } catch (error) {
    console.error('Failed to get progress keys:', error);
    return [];
  }
}

// ============================================================================
// Utility Helpers
// ============================================================================

/**
 * Format a timestamp for display
 *
 * @param timestamp - Unix timestamp (milliseconds)
 * @returns Formatted date string
 *
 * @example
 * ```ts
 * const timestamp = getProgressTimestamp('my-assessment-progress');
 * if (timestamp) {
 *   console.log(`Last saved: ${formatTimestamp(timestamp)}`);
 * }
 * ```
 */
export function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

/**
 * Format age in a human-readable way
 *
 * @param ageInDays - Age in days
 * @returns Human-readable age string
 *
 * @example
 * ```ts
 * const age = getProgressAge('my-assessment-progress');
 * if (age !== null) {
 *   console.log(`Progress from ${formatAge(age)}`);
 * }
 * // Output examples: "moments ago", "3 hours ago", "2 days ago"
 * ```
 */
export function formatAge(ageInDays: number): string {
  if (ageInDays < 0.01) {
    return 'moments ago';
  } else if (ageInDays < 1/24) {
    const minutes = Math.round(ageInDays * 24 * 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  } else if (ageInDays < 1) {
    const hours = Math.round(ageInDays * 24);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  } else if (ageInDays < 2) {
    return 'yesterday';
  } else if (ageInDays < 7) {
    const days = Math.round(ageInDays);
    return `${days} days ago`;
  } else if (ageInDays < 30) {
    const weeks = Math.round(ageInDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  } else {
    const months = Math.round(ageInDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
}
